import { productFamily } from "../../screens/LoanDrilldownScreen/experience/experienceConstants";

/**
 * Borrower classification override — shared rules.
 *
 * Backend contract: ../eastview-os/frontend_documentation/borrower-classification-override.md
 * (owner 2026-10-02) and app/routers/borrower_classification.py. The override is
 * edited ONLY through the modal (class + required justification note), posted to
 * POST /borrower_classification/override/{dealId} for both pipelines.
 */

export const OVERRIDE_FIELD = "borrower_classification_override";
export const OVERRIDE_NOTE_FIELD = "borrower_classification_override_note";

// Verbatim from `borrower_classification_override` json_schema_extra options
// (app/models/pipeline.py) / CLASSES in app/models/borrower_classification.py.
export const OVERRIDE_CLASS_OPTIONS = ["A+", "A", "B", "C"];

// The exception category that backs an override (exceptionCategory option).
export const BACKING_EXCEPTION_CATEGORY = "Borrower Classification";

/**
 * Does this product classify? Mirrors the backend's `applicable()` —
 * rtl_shape() is fix_and_flip / bridge / ground_up, with HVRTL and multifamily
 * variants taking a bridge-or-flip shape. DSCR / Other never classify (the
 * endpoint answers 409), so the override UI is hidden for them.
 */
export const classifiesProduct = (loanProductType) =>
  ["fix_and_flip", "bridge", "ground_up", "hvrtl", "multifamily"].includes(
    productFamily(loanProductType),
  );

// Same match as classification_override.backing_exception: a non-Rejected
// exception in the Borrower Classification category, or an older one whose
// text mentions the classification.
const MENTIONS = /\bclassification\b|\bborrower\s+class\b|\bA\+\s*class/i;

export const findBackingException = (loan) =>
  (loan?.exceptions || []).find((e) => {
    if (!e || (e.exceptionStatus || "Open") === "Rejected") return false;
    if (e.exceptionCategory === BACKING_EXCEPTION_CATEGORY) return true;
    const text = ["exceptionDescription", "description", "compensatingFactors"]
      .map((k) => (e[k] == null ? "" : String(e[k])))
      .join(" ");
    return MENTIONS.test(text);
  }) || null;

/** The read-back fields the card / rows render, in one place. */
export const getClassificationOverrideState = (loan) => {
  const details = loan?.borrowerClassificationDetails || null;
  const override =
    details?.overrideClass ?? loan?.[OVERRIDE_FIELD] ?? null;
  const calculated =
    loan?.borrower_classification_calculated ?? details?.borrowerClass ?? null;
  return {
    details,
    calculated,
    override,
    effective:
      loan?.borrower_classification_effective ??
      details?.effectiveClass ??
      override ??
      calculated,
    direction: details?.overrideDirection ?? null,
    note: loan?.[OVERRIDE_NOTE_FIELD] || null,
    by: loan?.borrower_classification_override_by || null,
    at: loan?.borrower_classification_override_at || null,
    needsException: !!override && !findBackingException(loan),
  };
};

/** "Calculated A (0 credit + 5 experience)" — the doc's side-by-side wording. */
export const calculatedLabel = (calculated, details) => {
  if (!calculated) return "Calculated —";
  // borrowerClassificationDetails.creditScore / experienceScore are the POINTS
  // (loan_classification in borrower_classification.py), not the FICO.
  const credit = details?.creditScore;
  const exp = details?.experienceScore;
  const pts =
    credit != null || exp != null
      ? ` (${credit ?? 0} credit + ${exp ?? 0} experience)`
      : "";
  return `Calculated ${calculated}${pts}`;
};

/** The 422/403/409 body → { field, message }. FastAPI nests it under `detail`. */
export const readOverrideError = (err) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const detail = data?.detail ?? data;
  const message =
    (typeof detail === "string" ? detail : detail?.error) ||
    (status === 403
      ? "Only underwriting or admin can change the classification override."
      : status === 409
        ? "This loan's product does not use borrower classification."
        : "The override could not be saved.");
  return { status, field: detail?.field || null, message };
};
